import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { Button } from '../../components/ui/Button.jsx';
import { Modal } from '../../components/ui/Modal.jsx';
import { ProductPicker } from '../../components/ui/ProductPicker.jsx';
import { useTranslation } from '../../i18n/useTranslation.js';
import { inventoryApi } from '../../services/inventoryApi.js';
import { salesApi } from '../../services/salesApi.js';
import { addDaysIso, formatCurrency, todayIso } from '../../utils/formatters.js';
import { unitShort } from '../../utils/units.js';
import { ProductCreateModal } from '../inventory/ProductCreateModal.jsx';

import { CustomerCreateModal } from './CustomerCreateModal.jsx';
import { usePriceResolver } from './usePriceResolver.js';

/* Dieselben Saetze wie bei den Offerten - der Auftrag uebernimmt sie
   sonst nicht, und die Rechnung bekaeme spaeter den Vorgabesatz. */
const TAX_RATES = ['8.1', '2.6', '3.8', '0'];

function emptyLine() {
  return { productId: '', description: '', quantity: '1', unit: '', unitPrice: '0', taxRate: '8.1', priceHint: null };
}
function emptyForm() {
  return {
    customerId: '',
    orderDate: todayIso(),
    requiredDate: addDaysIso(7),
    notes: '',
    lines: [emptyLine()],
  };
}

export function OrderCreateModal({ open, onClose }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [form, setForm] = useState(emptyForm);
  const [error, setError] = useState(null);
  const [customerModalOpen, setCustomerModalOpen] = useState(false);
  // Line index whose "+ Neu" opened the product quick-create, null = closed.
  const [productModalLine, setProductModalLine] = useState(null);

  const customers = useQuery({
    queryKey: ['sales', 'customers'],
    queryFn: () => salesApi.listCustomers().then((r) => r.items || []),
    enabled: open,
  });
  const products = useQuery({
    queryKey: ['inventory', 'products'],
    queryFn: () => inventoryApi.listProducts({ limit: 200 }).then((r) => r.items || []),
    enabled: open,
  });

  /* Netto je Zeile - die Steuer kommt oben drauf, wie im Dienst. Die
     Zeilenvorschau bleibt netto, damit sie zur spaeteren Belegzeile passt. */
  const lineTotals = form.lines.map((l) => Number(l.quantity || 0) * Number(l.unitPrice || 0));
  const grandTotal = lineTotals.reduce((acc, n) => acc + n, 0);

  const create = useMutation({
    mutationFn: (body) => salesApi.createOrder(body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sales', 'orders'] });
      setForm(emptyForm());
      setError(null);
      onClose();
    },
    onError: (err) => setError(err?.message || t('common.loadError')),
  });

  const { resolveLine, clearHints, invalidate } = usePriceResolver(setForm);

  const patchField = (key, value) => setForm((p) => ({ ...p, [key]: value }));
  const patchLine = (idx, key, value) =>
    setForm((p) => ({
      ...p,
      lines: p.lines.map((line, i) => (i === idx ? { ...line, [key]: value } : line)),
    }));
  const addLine = () => setForm((p) => ({ ...p, lines: [...p.lines, emptyLine()] }));
  const removeLine = (idx) => {
    invalidate(); // line indices shift - drop in-flight price resolutions
    setForm((p) => ({
      ...p,
      lines: p.lines.length > 1 ? p.lines.filter((_, i) => i !== idx) : p.lines,
    }));
  };

  // Shared by picker selection and inline product creation - the fresh
  // product is not in products.data yet, so always work from the object.
  // listPrice fallback applies immediately; the price-list resolution below
  // overrides it asynchronously when the customer has a matching tier.
  function applyProduct(idx, product) {
    setForm((p) => ({
      ...p,
      lines: p.lines.map((line, i) =>
        i === idx
          ? {
              ...line,
              productId: product.id,
              description: `${product.sku} - ${product.name}`,
              unit: product.unitOfMeasure || '',
              unitPrice: String(product.listPrice),
              priceHint: null,
            }
          : line,
      ),
    }));
    resolveLine(idx, { customerId: form.customerId, productId: product.id, quantity: form.lines[idx]?.quantity });
  }

  function handleProductPick(idx, productId) {
    const product = (products.data || []).find((p) => p.id === productId);
    if (product) applyProduct(idx, product);
  }

  function handleQuantityChange(idx, value) {
    patchLine(idx, 'quantity', value);
    resolveLine(idx, { customerId: form.customerId, productId: form.lines[idx]?.productId, quantity: value });
  }

  function handleCustomerChange(customerId) {
    patchField('customerId', customerId);
    clearHints();
    form.lines.forEach((line, idx) => {
      if (line.productId) {
        resolveLine(idx, { customerId, productId: line.productId, quantity: line.quantity });
      }
    });
  }

  function submit(e) {
    e.preventDefault();
    setError(null);
    // The picker is no native <select required>, so guard here.
    if (form.lines.some((l) => !l.productId)) {
      setError(t('picker.product.required'));
      return;
    }
    create.mutate({
      customerId: form.customerId,
      orderDate: form.orderDate,
      requiredDate: form.requiredDate || undefined,
      notes: form.notes || undefined,
      lines: form.lines.map((l) => ({
        productId: l.productId,
        description: l.description.trim() || ' ',
        quantity: Number(l.quantity || 0),
        unit: l.unit || undefined,
        unitPrice: Number(l.unitPrice || 0),
        taxRate: Number(l.taxRate || 0),
      })),
    });
  }

  return (
    <>
    <Modal
      open={open}
      onClose={onClose}
      title={t('sales.orders.modal.title')}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>{t('common.cancel')}</Button>
          <Button variant="primary" onClick={submit} disabled={create.isPending}>
            {create.isPending ? `${t('common.save')}…` : t('common.create')}
          </Button>
        </>
      }
    >
      <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
        {/* customer */}
        <label className="field">
          <span className="field-label">{t('sales.orders.modal.customer')}</span>
          <div className="inline-create">
            <select className="select" value={form.customerId} onChange={(e) => handleCustomerChange(e.target.value)} required>
              <option value="">{t('finance.invoices.modal.pickCounterparty')}</option>
              {(customers.data || []).map((c) => (
                <option key={c.id} value={c.id}>{c.code} - {c.name}</option>
              ))}
            </select>
            <Button type="button" variant="ghost" className="inline-create-btn" onClick={() => setCustomerModalOpen(true)}>
              + {t('common.new')}
            </Button>
          </div>
        </label>
        <div className="field-row">
          <label className="field">
            <span className="field-label">{t('sales.orders.modal.orderDate')}</span>
            <input className="input" type="date" value={form.orderDate} onChange={(e) => patchField('orderDate', e.target.value)} required />
          </label>
          <label className="field">
            <span className="field-label">{t('sales.orders.modal.requiredDate')}</span>
            <input className="input" type="date" value={form.requiredDate} onChange={(e) => patchField('requiredDate', e.target.value)} />
          </label>
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
          <span className="field-label">{t('sales.orders.modal.lines')}</span>
          <Button size="sm" variant="ghost" onClick={addLine}>+ {t('finance.invoices.modal.addLine')}</Button>
        </div>
        {form.lines.map((line, idx) => (
          <div
            key={idx}
            style={{
              display: 'flex', flexDirection: 'column', gap: 'var(--space-2)',
              padding: 'var(--space-3)', border: '1px solid var(--color-hairline)',
              borderRadius: 'var(--radius-md)',
            }}
          >
            <div className="field">
              <span className="field-label">{t('sales.orders.modal.product')}</span>
              <div className="inline-create">
                <ProductPicker
                  products={products.data || []}
                  value={line.productId}
                  valueLabel={line.description}
                  onPick={(id) => handleProductPick(idx, id)}
                  onCreateNew={() => setProductModalLine(idx)}
                />
                <Button type="button" variant="ghost" className="inline-create-btn" onClick={() => setProductModalLine(idx)}>
                  + {t('common.new')}
                </Button>
              </div>
            </div>
            <label className="field">
              <span className="field-label">{t('sales.orders.modal.description')}</span>
              <input className="input" value={line.description} onChange={(e) => patchLine(idx, 'description', e.target.value)} required />
            </label>
            <div className="field-row">
              <label className="field">
                <span className="field-label">{t('sales.orders.modal.quantity')}{line.unit ? ` (${unitShort(t, line.unit)})` : ''}</span>
                <input
                  className="input"
                  type="number"
                  min="0.0001"
                  step="any"
                  value={line.quantity}
                  onChange={(e) => handleQuantityChange(idx, e.target.value)}
                  required
                />
              </label>
              <label className="field">
                <span className="field-label">{t('sales.orders.modal.unitPrice')}</span>
                <input
                  className="input"
                  type="number"
                  min="0"
                  step="0.01"
                  value={line.unitPrice}
                  onChange={(e) => patchLine(idx, 'unitPrice', e.target.value)}
                  required
                />
              </label>
              {/* Der Satz JE ZEILE. Er fehlte hier, und damit lief jeder direkt
                  erfasste Auftrag ohne Steuer durch - bis die Rechnung ihn mit
                  dem Vorgabesatz belegte. */}
              <label className="field" style={{ flex: '0 0 90px' }}>
                <span className="field-label">{t('vat.rateCol')}</span>
                <select
                  className="select"
                  value={line.taxRate}
                  onChange={(e) => patchLine(idx, 'taxRate', e.target.value)}
                >
                  {TAX_RATES.map((rate) => (
                    <option key={rate} value={rate}>{rate} %</option>
                  ))}
                </select>
              </label>
            </div>
            {line.priceHint && (
              <span className="muted text-sm">
                {t('priceList.applied', { name: line.priceHint.name, minQuantity: line.priceHint.minQuantity })}
              </span>
            )}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span className="muted text-sm">
                {t('sales.orders.modal.lineTotal')}: <strong>{formatCurrency(lineTotals[idx])}</strong>
              </span>
              {form.lines.length > 1 && (
                <Button size="sm" variant="danger" onClick={() => removeLine(idx)}>
                  {t('finance.invoices.modal.removeLine')}
                </Button>
              )}
            </div>
          </div>
        ))}

        <div style={{
          display: 'flex', justifyContent: 'space-between',
          borderTop: '1px solid var(--color-hairline)', paddingTop: 'var(--space-3)',
        }}>
          <span>{t('sales.orders.modal.orderTotal')}</span>
          <strong className="mono">{formatCurrency(grandTotal)}</strong>
        </div>

        <label className="field">
          <span className="field-label">{t('sales.orders.modal.notes')}</span>
          <textarea className="textarea" rows={2} value={form.notes} onChange={(e) => patchField('notes', e.target.value)} />
        </label>

        {error && <div className="alert danger">{error}</div>}
      </form>
    </Modal>
    <CustomerCreateModal
      open={customerModalOpen}
      onClose={() => setCustomerModalOpen(false)}
      onCreated={(created) => {
        queryClient.invalidateQueries({ queryKey: ['sales', 'customers'] });
        if (created?.id) {
          patchField('customerId', created.id);
        }
      }}
    />
    <ProductCreateModal
      open={productModalLine != null}
      onClose={() => setProductModalLine(null)}
      onCreated={(created) => {
        if (created?.id && productModalLine != null) {
          applyProduct(productModalLine, created);
        }
      }}
    />
    </>
  );
}
